import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '@/lib/api-client';
import type { ApiResponse } from '@/lib/api-client';
import { clientLedgerKeys } from './useClientLedger';

// ─── Types ──────────────────────────────────────────────────────────────────

export interface AccountClosing {
  id: string;
  tenant_id: string;
  client_id: string;
  period_start: string;
  period_end: string;
  opening_balance: number;
  closing_balance: number;
  /** User's manual override of the closing balance. Null if no adjustment was made. */
  adjusted_closing_balance: number | null;
  /** Difference between adjusted and system-calculated. Null if no adjustment. */
  adjustment_amount: number | null;
  total_debits: number;
  total_credits: number;
  status: 'closed' | 'reopened';
  notes: string | null;
  closed_by_id: string;
  closed_at: string;
  reopened_by_id: string | null;
  reopened_at: string | null;
  reopen_reason: string | null;
  created_at: string;
  updated_at: string;
}

export interface AccountClosingListResponse {
  data: AccountClosing[];
  pagination: {
    page: number;
    pageSize: number;
    total: number;
    totalPages: number;
    hasNext: boolean;
    hasPrevious: boolean;
  };
}

export interface BalanceSummary {
  opening_balance: number;
  closing_balance: number;
  total_debits: number;
  total_credits: number;
  last_closed_period: string | null;
  has_unclosed_transactions: boolean;
}

export interface CloseAccountInput {
  clientId: string;
  period_start: string;
  period_end: string;
  notes?: string;
}

export interface ReopenPeriodInput {
  closingId: string;
  reason: string;
}

export interface AccountClosingListParams {
  clientId: string;
  status?: 'closed' | 'reopened';
  page?: number;
  pageSize?: number;
}

// ─── Query Keys ─────────────────────────────────────────────────────────────

export const accountClosingKeys = {
  all: ['billing', 'account-closings'] as const,
  lists: () => [...accountClosingKeys.all, 'list'] as const,
  list: (params: AccountClosingListParams) => [...accountClosingKeys.lists(), params] as const,
  detail: (id: string) => [...accountClosingKeys.all, 'detail', id] as const,
  balanceSummary: (clientId: string) =>
    [...accountClosingKeys.all, 'balance-summary', clientId] as const,
};

// ─── Hooks ──────────────────────────────────────────────────────────────────

/**
 * Fetch account closing history for a client.
 */
export function useAccountClosingHistory(params: AccountClosingListParams) {
  return useQuery({
    queryKey: accountClosingKeys.list(params),
    queryFn: async () => {
      const response = await apiClient.get<{
        success: boolean;
        data: AccountClosing[];
        pagination: {
          page: number;
          pageSize: number;
          total: number;
          totalPages: number;
          hasNext: boolean;
          hasPrevious: boolean;
        };
      }>(`/billing/clients/${params.clientId}/account-closings`, {
        params: {
          status: params.status || undefined,
          page: params.page ?? 1,
          pageSize: params.pageSize ?? 10,
        },
      });
      return response.data.data;
    },
    enabled: !!params.clientId,
  });
}

/**
 * Fetch balance summary for a client (opening/closing, last closed period).
 */
export function useBalanceSummary(clientId: string) {
  return useQuery({
    queryKey: accountClosingKeys.balanceSummary(clientId),
    queryFn: async () => {
      const response = await apiClient.get<{ success: boolean; data: BalanceSummary }>(
        `/billing/clients/${clientId}/balance-summary`,
      );
      return response.data.data;
    },
    enabled: !!clientId,
  });
}

/**
 * Close a client account period.
 */
export function useCloseAccount() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (input: CloseAccountInput & { adjusted_closing_balance?: number | null }) => {
      const response = await apiClient.post<{ success: boolean; data: AccountClosing }>(
        '/billing/account-closings',
        {
          client_id: input.clientId,
          period_start: input.period_start,
          period_end: input.period_end,
          notes: input.notes || undefined,
          adjusted_closing_balance: input.adjusted_closing_balance ?? undefined,
        },
      );
      return response.data.data;
    },
    onSuccess: (_data, variables) => {
      // Invalidate closing history and balance summary
      queryClient.invalidateQueries({ queryKey: accountClosingKeys.lists() });
      queryClient.invalidateQueries({
        queryKey: accountClosingKeys.balanceSummary(variables.clientId),
      });
      // Also invalidate ledger data as balance info changed
      queryClient.invalidateQueries({ queryKey: clientLedgerKeys.all });
    },
  });
}

/**
 * Preview the system-calculated closing balance for a period (without committing).
 * Uses the balance-summary and ledger data to compute what the closing balance would be.
 */
export function usePreviewClosingBalance(clientId: string, periodStart: string, periodEnd: string) {
  return useQuery({
    queryKey: [...accountClosingKeys.all, 'preview', clientId, periodStart, periodEnd],
    queryFn: async () => {
      // Fetch ledger entries for the period to calculate totals
      const response = await apiClient.get<{
        success: boolean;
        data: Array<{ amount: number }>;
        summary: { total_debits: number; total_credits: number; current_balance: number };
      }>(`/billing/clients/${clientId}/ledger`, {
        params: {
          date_from: periodStart,
          date_to: periodEnd,
          pageSize: 1,
        },
      });

      return {
        total_debits: response.data.summary?.total_debits ?? 0,
        total_credits: response.data.summary?.total_credits ?? 0,
        current_balance: response.data.summary?.current_balance ?? 0,
      };
    },
    enabled: !!clientId && !!periodStart && !!periodEnd,
  });
}

/**
 * Reopen a previously closed period.
 */
export function useReopenPeriod() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (input: ReopenPeriodInput) => {
      const response = await apiClient.post<ApiResponse<AccountClosing>>(
        `/billing/account-closings/${input.closingId}/reopen`,
        { reason: input.reason },
      );
      return response.data.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: accountClosingKeys.lists() });
      queryClient.invalidateQueries({ queryKey: accountClosingKeys.all });
      queryClient.invalidateQueries({ queryKey: clientLedgerKeys.all });
    },
  });
}

/**
 * Download client ledger as PDF.
 */
export function useDownloadLedgerPdf() {
  return useMutation({
    mutationFn: async ({
      clientId,
      startDate,
      endDate,
      entryType,
    }: {
      clientId: string;
      startDate?: string;
      endDate?: string;
      entryType?: string;
    }) => {
      const response = await apiClient.get(`/billing/clients/${clientId}/ledger/pdf`, {
        params: {
          start_date: startDate || undefined,
          end_date: endDate || undefined,
          entry_type: entryType || undefined,
        },
        responseType: 'blob',
      });
      // Trigger PDF download
      const blob = new Blob([response.data as BlobPart], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `client-ledger-${clientId}-${Date.now()}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    },
  });
}
